import { map } from 'ramda';
import { useMemo, useRef } from 'react';
import { queueScheduler } from 'rxjs';
import { mediaManagerFileClick } from '@wix/bi-logger-media-data/v2';
import { mediaManagerFileClickParams } from '@wix/bi-logger-media-data/v2/types';
import { bi } from '../../../../services/loggers/bi';
import { extractCommonBiFieldsFromItems, extractMetaSiteId } from '../../../bi';
import { ItemSelectOrigin, SelectItemAdditionalParams } from './types';
import { MediaManagerSDKAnyItem } from '../../../media-manager-sdk';
import { useFileType } from '../useFileType';
import { useSearchResultsBI } from '../../../../containers/FilesManager/SearchResults/useSearchResultsBI';
import { useRelatedItems } from '../../../hoc/withRelatedItems/withRelatedItems';
import { SelectedItemType, SelectedItemTypes } from '../../../hoc/withItemsReducer/withItemsReducer.types';

export const useTrackItemSelectAnalytics = () => {
  const clicksCount = useRef(0);
  const fileType = useFileType();
  const { searchMetaInfo, searchId } = useSearchResultsBI();
  const { relatedItemsMetaInfo } = useRelatedItems();

  return useMemo(
    () => ({
      trackItemsSelected: (
        items: MediaManagerSDKAnyItem[],
        params: Partial<SelectItemAdditionalParams>,
        selectedItemTypes?: SelectedItemTypes,
      ) => {
        clicksCount.current += 1;

        const biRequests = map((item): mediaManagerFileClickParams => {
          const isRelatedItem = selectedItemTypes?.[item.id] === SelectedItemType.RelatedItem;
          const metaInfo = isRelatedItem ? relatedItemsMetaInfo : searchMetaInfo;
          const itemMetaInfo = metaInfo?.[item.id];

          let sectionParams = {};

          if (itemMetaInfo) {
            sectionParams = {
              section_name: itemMetaInfo.sectionName,
              section_index: itemMetaInfo.sectionIndex,
              clicked_file_index: itemMetaInfo.itemIndexWithinCategory,
            };
          }

          return {
            ...params,
            ...extractCommonBiFieldsFromItems(item, { fileType }),
            ...extractMetaSiteId(item),
            search_id: searchId,
            ...sectionParams,
          };
        }, items);

        // TODO: remove usage of queueScheduler
        queueScheduler.schedule(
          function (requestIndex: number) {
            if (requestIndex > -1) {
              if (biRequests[requestIndex].clickOrigin === ItemSelectOrigin.Click) {
                this.schedule(requestIndex - 1);

                if (!clicksCount) {
                  return;
                }

                if (clicksCount.current > 1) {
                  bi.logger().report(
                    mediaManagerFileClick({
                      ...biRequests[requestIndex],
                      clickOrigin: ItemSelectOrigin.DoubleClick,
                    }),
                  );
                  clicksCount.current = 0;
                  return;
                }

                bi.logger().report(mediaManagerFileClick(biRequests[requestIndex]));
                clicksCount.current = 0;
                return;
              }

              const scheduleNext = () => this.schedule(requestIndex - 1);
              bi.logger().report(mediaManagerFileClick(biRequests[requestIndex])).then(scheduleNext, scheduleNext);
            }
          },
          200,
          biRequests.length - 1,
        );
      },
    }),
    [fileType, searchId, searchMetaInfo, relatedItemsMetaInfo],
  );
};
